/**
 * Location Service
 *
 * Servicio para obtener estados/provincias y ciudades desde el backend.
 * Los datos provienen de dr5hn/countries-states-cities-database.
 *
 * Endpoints:
 * - GET /api/locations/states?country=ES
 * - GET /api/locations/cities?country=ES&state=M
 */

import { apiClient } from './apiClient';

export interface State {
  name: string;
  iso2: string;
  type?: string;
}

export interface City {
  name: string;
  latitude?: string | null;
  longitude?: string | null;
}

interface StatesResponse {
  success: boolean;
  data: State[];
  count: number;
}

interface CitiesResponse {
  success: boolean;
  data: City[];
  count: number;
}

/**
 * Obtiene los estados/provincias de un país
 *
 * @param countryCode Código ISO2 del país (ej: ES, MX, AR)
 * @returns Lista de estados ordenados alfabéticamente
 *
 * Nota: Para España, devuelve solo las 50 provincias (no comunidades autónomas)
 */
export async function getStates(countryCode: string): Promise<State[]> {
  try {
    const response = await apiClient.get<State[]>(
      `/locations/states?country=${encodeURIComponent(countryCode)}`
    );

    if (response.success && response.data) {
      return response.data;
    }

    console.error('[locationService] Error getting states:', response.error);
    return [];
  } catch (error) {
    console.error('[locationService] Error getting states:', error);
    return [];
  }
}

/**
 * Obtiene las ciudades de un estado/provincia
 *
 * @param countryCode Código ISO2 del país
 * @param stateCode Código ISO2 del estado
 * @returns Lista de ciudades ordenadas alfabéticamente
 *
 * Nota: Excluye distritos y barrios (type="section")
 */
export async function getCities(countryCode: string, stateCode: string): Promise<City[]> {
  try {
    const response = await apiClient.get<City[]>(
      `/locations/cities?country=${encodeURIComponent(countryCode)}&state=${encodeURIComponent(stateCode)}`
    );

    if (response.success && response.data) {
      return response.data;
    }

    console.error('[locationService] Error getting cities:', response.error);
    return [];
  } catch (error) {
    console.error('[locationService] Error getting cities:', error);
    return [];
  }
}
